import { useTheme as useStyledComponentsTheme } from 'styled-components';
import { intersection } from 'lodash';

import { useAppSelector } from 'hooks/redux';
import { useIsNewDesign } from 'hooks/useIsNewDesign';
import { resolveDesignVariables } from 'helpers/designVariables';
import { theme as defaultTheme } from 'assets/css/theme';
import { useBreakpoint } from 'hooks/useBreakpoint';

// The redesign's own colour surface. Redesign code reads ONLY this — never
// `colors.*`, whose keys are legacy names kept alive by helpers/designVariables. When the
// legacy stylesheet goes, `colors` shrinks to nothing and this survives untouched.
export interface IPalette {
  pageBackground: string;
  pageText: string;
  // "Designelemente" in the builder: the filter, selection bars, modals, lightbox and
  // mobile flybar — everything but the page itself.
  uiBackground: string;
  uiText: string;
  accent: string;
  // Gallery name, date and photographer credit on the cover
  coverText: string;
}

export interface ITheme {
  colors: {
    [key: string]: string;
  };
  palette: IPalette;
  isSecondaryColor?: boolean;
  inputBorderRadius: number;
}

export const useThemeHelper = (): Array<ITheme> => {
  const isShop = intersection(window.location.pathname.split('/'), ['shop']).length;
  const { design, templateName, type } = useAppSelector((state) => state.collection);
  const isDesktop = useBreakpoint('desktopXl');
  const isNewDesign = useIsNewDesign();

  if (isShop) return [defaultTheme];

  const variables = resolveDesignVariables(design, isNewDesign);

  const primaryColor = variables?.primaryColor as string;
  const secondaryColor = variables?.secondaryColor as string;

  const isDarkTemplate = templateName === 'dark' && type === 'ScrAppBook2';

  const isClassic = templateName === 'dark' && type === 'ScrAppBook';
  const lightTemplates = ['SimpleFiligree', 'Breeze', 'VickyBaumannFineart'];

  const isSecondaryColor =
    !isDesktop &&
    !isClassic &&
    !lightTemplates.includes(templateName) &&
    !templateName.startsWith('SpringVibes') &&
    !isDarkTemplate;

  const theme: ITheme = {
    colors: {
      primary: primaryColor,
      secondary: secondaryColor,
      mobileBorderColor: isDarkTemplate ? primaryColor : secondaryColor,
      mobileIndicatorColor: isDarkTemplate ? secondaryColor : primaryColor,
      accent: (isNewDesign && (variables?.accentColor as string)) || '#455773',
      accentHover: '#576e91',
      white: '#fff',
      black: '#000',
      ...variables
    },
    // Every token resolves to something as long as primary/secondary exist, so consumers
    // never need a `||` fallback of their own. The legacy keys in the middle of each chain
    // are the bridge for pre-redesign collections and go with the rest of it.
    palette: {
      pageBackground: (primaryColor || variables?.pageBg) as string,
      pageText: (secondaryColor || variables?.fontColor) as string,
      uiBackground: (variables?.uiBackgroundColor || variables?.menuBg || primaryColor) as string,
      uiText: (variables?.uiTextColor || variables?.menuIconsColor || secondaryColor) as string,
      accent: (variables?.accentColor || variables?.decorationsColor || secondaryColor) as string,
      // Deliberately not defaulted here: see IPalette#coverText.
      coverText: (variables?.coverTextColor as string) || ''
    },
    isSecondaryColor,
    inputBorderRadius: variables?.inputBorderRadius as number
  };

  return [theme];
};

export const useTheme = (): ITheme[] => {
  const theme = useStyledComponentsTheme() as ITheme;

  if (!theme) return [defaultTheme];

  return [theme];
};
